// --- React
import React, { useState } from 'react';
// --- Hooks
import { useDialogsActions } from '../hooks';
import { useDialogsForm } from '../forms';
// --- UI Components
import Select from '@dtci/spellbook/Select';
// --- API
import { datastageApi } from '../../../api';

/**
 * SharedSectionsForm Component
 *
 * This component renders a form for selecting shared sections.
 * It includes a select input for choosing shared sections.
 * It uses the Select component for section selection.
 *
 * @component
 * @param {Object} options - The options object containing shared sections.
 * * @returns {JSX.Element} The rendered SharedSectionsForm component.
 */
const SharedSectionsForm = ({ options }) => {
    const form = useDialogsForm();
    const [selectedSection, setSelectedSection] = useState(null);

    // Function to handle the change event of the select input
    const handleOnchange = (data) => {
        form.setValue('section', data);
        setSelectedSection(data);
    };

    // Function to load options for the select input
    const loadOptions = async (inputValue, callback) => {
        if (inputValue.length > 2) {
            const sharedSections = [];

            // Fetch shared sections from the API based on the input value
            const data = await datastageApi.sharedSections(
                {
                    q: inputValue,
                },
                [0, 24],
                ['id', 'DESC'],
            );

            // Map the response to create options for the select input
            data?.content.forEach((x) => {
                sharedSections.push({
                    label: x.title,
                    value: x.id,
                });
            });

            // Update the shared sections options
            callback(sharedSections);
        }
    };

    return (
        <div className="flexBox">
            <Select
                async
                onChange={(e) => handleOnchange(e)}
                name="section"
                defaultOptions={options}
                loadOptions={loadOptions}
                useMenuPortal
                isSearchable
                isClearable
                value={selectedSection}
            />
        </div>
    );
};

/**
 * useSharedSectionDialog Hook
 *
 * This hook provides functionality to open a shared section selection dialog.
 * It uses the useDialogsActions and useDialogsForm hooks to manage the dialog state.
 * It also provides a handleSubmit function to handle the form submission.
 *
 * @returns {Object} An object containing the openSharedSectionDialog function.
 */
export const useSharedSectionDialog = () => {
    const { openDialog, closeDialog } = useDialogsActions();
    const form = useDialogsForm();

    // Function to handle form submission
    const handleSubmit = (onSubmit) => () => {
        onSubmit(form.state.formData);
        form.reset();
        closeDialog();
    };

    // Function to open the shared section dialog
    const openSharedSectionDialog = (options, title, onConfirm) =>
        openDialog({
            title: `Select Shared Section`,
            content: <SharedSectionsForm options={options} />,
            closeAction: true,
            primaryAction: {
                label: 'Add Shared Section',
                action: handleSubmit(onConfirm),
            },
        });

    return { openSharedSectionDialog };
};
